import React from 'react';
import { StandardGrid } from './StandardGrid';
import { Column, Summary, TotalItem, CustomStore, MasterDetail } from './StandardGrid';
import { GridSettingType } from '@apply/constants';
import dxDataGrid, {
    CellClickEvent,
    CellDblClickEvent,
    CellPreparedEvent,
    Column as DevExtremeColumn,
    InitNewRowEvent,
    RowDblClickEvent,
    RowExpandingEvent,
    RowPreparedEvent,
    RowRemovedEvent,
} from 'devextreme/ui/data_grid';
import { EventInfo } from 'devextreme/events';

export { Column, Summary, TotalItem, CustomStore, MasterDetail };

export interface BandInfo {
    id: number;
    display: string;
    bandId: number;
    members: string[];
}

export interface BandGridProps {
    children?: React.ReactNode;
    gridType?: GridSettingType;
    dataStore?: CustomStore;
    dataSource?: any;
    allowedPageSizes?: number[];
    defaultPageSize?: number;
    onCellPrepared?: (e: CellPreparedEvent<unknown, unknown>) => void;
    onSelectionChanged?: (e: EventInfo<dxDataGrid<unknown, unknown>>) => void;
    onCellClick?: (e: CellClickEvent<unknown, unknown>) => void;
    onCellDblClick?: (e: CellDblClickEvent<unknown, unknown>) => void;
    onRowDblClick?: (e: RowDblClickEvent<unknown, unknown>) => void;
    customizeColumns?: (columns: DevExtremeColumn<unknown, unknown>[]) => void;
    onContentReady?: (e: EventInfo<dxDataGrid<unknown, unknown>>) => void;
    allowColumnReordering?: boolean;
    onRowPrepared?: (e: RowPreparedEvent<unknown, unknown>) => void;
    onRowRemoved?: (e: RowRemovedEvent<unknown, unknown>) => void;
    onRowExpanding?: (e: RowExpandingEvent<unknown, unknown>) => void;
    onInitNewRow?: (e: InitNewRowEvent<unknown, unknown>) => void;
    wordWrapEnabled?: boolean;
    className?: string;
    width?: number;
    keyExpr?: string;
    allowColumnResizing?: boolean;
    pagerVisible?: boolean;
    showColumnHeaders?: boolean;
    leadPanelEnabled?: boolean;
    loadPanelEnabled?: boolean;
    showBorders?: boolean;
    showRowLines?: boolean;
    rowAlternationEnabled?: boolean;
    selectionMode?: string;
    remoteOperations?: boolean;
    noDataText?: string;
    height?: any;
    bandInfo?: BandInfo[];
}

export const BandGrid: React.FC<BandGridProps> = ({
    className = 'bandStyle',
    bandInfo,
    children,
    onCellPrepared,
    ...remainingProps
}) => {
    const childArray = React.Children.toArray(children);

    // Build lookup: columnKey → bandId
    const bandLookup = new Map<string, number>();
    bandInfo?.forEach((band) => {
        band.members.forEach((key) => {
            bandLookup.set(key, band.bandId);
        });
    });

    // Separate unbanded vs banded columns
    const unbandedChildren: any[] = [];
    const bandedChildren: any[] = [];

    childArray.forEach((child: any) => {
        const key = child.props?.dataField || child.key;
        if (bandLookup.has(key)) bandedChildren.push(child);
        else unbandedChildren.push(child);
    });

    // Sort banded children by bandId then alphabetically
    bandedChildren.sort((a: any, b: any) => {
        const aKey = a.props?.dataField || a.key;
        const bKey = b.props?.dataField || b.key;
        const aBand = bandLookup.get(aKey)!;
        const bBand = bandLookup.get(bKey)!;
        if (aBand !== bBand) return aBand - bBand;
        return String(aKey).localeCompare(String(bKey));
    });

    //Output used for the children on the grid
    const sortedChildren = [...unbandedChildren, ...bandedChildren];

    // Build band → ordered column list
    const bandColumns = new Map<number, string[]>();
    bandedChildren.forEach((child) => {
        const key = child.props?.dataField || child.key;
        const bandId = bandLookup.get(key)!;
        if (!bandColumns.has(bandId)) bandColumns.set(bandId, []);
        bandColumns.get(bandId)!.push(key);
    });

    // Inject band classes into <td>
    const handleCellPrepared = (e: any) => {
        if (onCellPrepared) onCellPrepared(e);

        const col = e.column;
        if (!col) return;

        const key = col.dataField || col.name;
        const bandId = bandLookup.get(key);
        if (!bandId) return;

        // Apply band class
        e.cellElement.classList.add(`bandGrid-band-${bandId}`);

        // Determine first/last column in this band
        const cols = bandColumns.get(bandId);
        if (!cols) return;

        if (cols[0] === key) {
            e.cellElement.classList.add(`bandGrid-band-${bandId}-first`);
        }

        if (cols[cols.length - 1] === key) {
            e.cellElement.classList.add(`bandGrid-band-${bandId}-last`);
        }
    };

    return (
        <StandardGrid
            className={className}
            onCellPrepared={handleCellPrepared}
            {...remainingProps}
        >
            {sortedChildren}
        </StandardGrid>
    );
};
